import { createContext, useContext, useState, useEffect, ReactNode } from 'react'
import { api } from '../services/api'
import { useAuth } from '../hooks/useAuth'

interface TicketCountContextType {
  pendingCount: number
  refreshCount: () => Promise<void>
}

const TicketCountContext = createContext<TicketCountContextType | undefined>(undefined)

export const TicketCountProvider = ({ children }: { children: ReactNode }) => {
  const { user } = useAuth()
  const [pendingCount, setPendingCount] = useState(0)

  const fetchPendingCount = async () => {
    if (user?.role) {
      try {
        // More efficient: fetch count of non-closed tickets in a single request
        const response = await api.get('/tickets/count', {
          params: {
            excludeStatus: 'CLOSED'
          }
        })
        setPendingCount(response.data.count || 0)
      } catch (error) {
        // Fallback to the old method if the new endpoint doesn't exist
        try {
          const statuses = ['OPEN', 'IN_PROGRESS', 'PENDING_CUSTOMER', 'RESOLVED', 'REOPENED']
          let totalCount = 0
          
          for (const status of statuses) {
            const response = await api.get('/tickets', {
              params: { status, pageSize: 1 }
            })
            totalCount += response.data.pagination?.total || 0
          }
          
          setPendingCount(totalCount)
        } catch (fallbackError) {
          console.error('Failed to fetch pending tickets count:', fallbackError)
        }
      }
    }
  }

  const refreshCount = async () => {
    await fetchPendingCount()
  }

  useEffect(() => {
    fetchPendingCount()
    
    // Refresh count every 10 seconds (reduced from 30)
    const interval = setInterval(fetchPendingCount, 10000)
    
    // Listen for custom refresh events
    const handleRefresh = () => {
      fetchPendingCount()
    }
    
    window.addEventListener('ticket-status-changed', handleRefresh)
    window.addEventListener('ticket-created', handleRefresh)
    window.addEventListener('ticket-deleted', handleRefresh)
    
    return () => {
      clearInterval(interval)
      window.removeEventListener('ticket-status-changed', handleRefresh)
      window.removeEventListener('ticket-created', handleRefresh)
      window.removeEventListener('ticket-deleted', handleRefresh)
    }
  }, [user])

  return (
    <TicketCountContext.Provider value={{ pendingCount, refreshCount }}>
      {children}
    </TicketCountContext.Provider>
  )
}

export const useTicketCount = () => {
  const context = useContext(TicketCountContext)
  if (context === undefined) {
    throw new Error('useTicketCount must be used within a TicketCountProvider')
  }
  return context
}

